import dayjs from 'dayjs';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';

import { PlatformApp } from '@typings';
import { platformAppsTemplateSelector } from '@selectors';
import { useSelector } from '@hooks';
import { path } from '@utils';

import { Icon, Link } from '@components/Shared';

import { BadgeStatus } from '../BadgeStatus';

import { PlatformAppInstanceUrls } from './PlatformAppInstanceUrls';
import { PlatformAppLogo } from './PlatformAppLogo';

type Props = Pick<
  PlatformApp.Instance,
  | 'id'
  | 'name'
  | 'displayName'
  | 'state'
  | 'creator'
  | 'templateName'
  | 'templateVersion'
  | 'createdAt'
  | 'endpoints'
> & {
  className?: string;
};

export const PlatformAppInstalledItem: React.FC<Props> = ({
  id: instanceId,
  name,
  displayName,
  state,
  creator,
  templateName,
  templateVersion,
  createdAt,
  endpoints,
}) => {
  const template = useSelector(platformAppsTemplateSelector(templateName));
  const title = displayName || name;

  return (
    <div className="relative flex min-h-[160px] flex-col gap-3 rounded-xl bg-white p-6">
      <div className="mb-4 flex items-start gap-6">
        <PlatformAppLogo src={template?.logo} size="md" />
        <div className="w-full min-w-0">
          <div className="flex items-center justify-between gap-4">
            <p className="mr-auto truncate text-h6 text-black" title={title}>
              {title}
            </p>
            <BadgeStatus status={state} />
          </div>
          <p className="text-body-medium text-neural-04">{templateName}</p>
        </div>
      </div>
      <div className="mt-auto flex gap-6 overflow-hidden">
        <div className="flex flex-col gap-1">
          <p className="text-footnote text-neural-04">Installed</p>
          <p className="truncate">{dayjs(createdAt).fromNow()}</p>
        </div>
        <div className="flex flex-1 flex-col gap-1">
          <p className="text-footnote text-neural-04">Owner</p>
          <p className="truncate">{creator}</p>
        </div>
        <div className="ml-auto flex flex-col gap-1">
          <p className="text-footnote text-neural-04">Version</p>
          <p className="truncate">{templateVersion}</p>
        </div>
      </div>
      <div className="flex justify-end gap-3">
        <Link
          className="h-10 flex-1 capitalize"
          variant="secondary"
          to={path.appsInstance(instanceId)}
        >
          Details
          <Icon size="lg" icon={faArrowRight} />
        </Link>
        <PlatformAppInstanceUrls
          variant="secondary"
          size="small"
          urls={endpoints}
        />
      </div>
    </div>
  );
};
